import React, { useState, useRef, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";

interface VariablePickerProps {
  onInsert: (variable: string) => void;
}

interface CustomField {
  id?: number;
  name?: string;
  label?: string;
  scope?: string;
  type?: string;
}

interface VariableItem {
  name: string;
  label: string;
  scope: "built-in" | "contact" | "conversation" | "workflow";
  description?: string;
}

const SCOPE_DESCRIPTIONS: Record<string, string> = {
  "built-in": "Always available — no setup needed",
  contact: "Permanent — remembered across all future chats",
  conversation: "This chat only — cleared when chat ends",
  workflow: "Step only — gone when workflow finishes"
};

const BUILT_IN_VARIABLES: VariableItem[] = [
  {
    name: "name",
    label: "Full Name",
    scope: "built-in",
    description: "Visitor's full name from prechat or contact record"
  },
  {
    name: "firstName",
    label: "First Name",
    scope: "built-in",
    description: "Visitor's first name"
  },
  {
    name: "lastName",
    label: "Last Name",
    scope: "built-in",
    description: "Visitor's last name"
  },
  {
    name: "email",
    label: "Email",
    scope: "built-in",
    description: "Visitor's email from prechat or contact record"
  },
  {
    name: "phone",
    label: "Phone",
    scope: "built-in",
    description: "Visitor's phone number"
  },
  {
    name: "language",
    label: "Language",
    scope: "built-in",
    description: "Visitor's browser language (e.g. en, es, fr)"
  }
];

const SCOPE_LABELS: Record<string, string> = {
  "built-in": "Built-in",
  contact: "Permanent",
  conversation: "This Chat",
  workflow: "Step Only"
};

const SCOPE_COLORS: Record<string, string> = {
  "built-in": "bg-slate-100 text-slate-600",
  contact: "bg-blue-100 text-blue-700",
  conversation: "bg-amber-100 text-amber-700",
  workflow: "bg-green-100 text-green-700"
};

export default function VariablePicker({ onInsert }: VariablePickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [customFields, setCustomFields] = useState<VariableItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [hoveredVar, setHoveredVar] = useState<VariableItem | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const api = useApi();

  const fetchCustomFields = useCallback(async () => {
    if (loaded) return;
    try {
      const result = await api.messaging.get("ContactFieldDefinitions/list");
      if (result.ok) {
        const data = await result.json();
        const defs: CustomField[] = Array.isArray(data)
          ? data
          : Array.isArray(data?.definitions)
            ? data.definitions
            : [];
        const mapped: VariableItem[] = defs.map((d) => ({
          name: d.name || d.label?.toLowerCase().replace(/\s+/g, "_") || "",
          label: d.label || d.name || "",
          scope: (d.scope as VariableItem["scope"]) || "conversation",
          description: `Custom ${d.type || "text"} field`
        }));
        setCustomFields(mapped);
      }
    } catch {
      // API unavailable — show built-ins only
    }
    setLoaded(true);
  }, [api.messaging, loaded]);

  useEffect(() => {
    if (open) {
      fetchCustomFields();
    }
  }, [open, fetchCustomFields]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setSearch("");
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const allVariables = [...BUILT_IN_VARIABLES, ...customFields];

  const filtered = search.trim()
    ? allVariables.filter(
        (v) =>
          v.name.toLowerCase().includes(search.toLowerCase()) ||
          v.label.toLowerCase().includes(search.toLowerCase())
      )
    : allVariables;

  const groups = ["built-in", "contact", "conversation", "workflow"] as const;

  function handleSelect(v: VariableItem) {
    onInsert(`<<${v.name}>>`);
    setOpen(false);
    setSearch("");
  }

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 px-2 h-7 rounded border border-slate-300 bg-white hover:bg-blue-50 text-xs text-slate-500 hover:text-blue-600 hover:border-blue-400 transition-colors whitespace-nowrap"
        title="Insert variable (<<variable>>)"
        aria-label="Insert variable"
      >
        <span className="font-mono text-[11px]">{"<<"}</span>
        <span className="text-[11px]">var</span>
        <span className="font-mono text-[11px]">{">>"}</span>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-80 bg-white rounded-lg border border-slate-200 shadow-xl z-50 max-h-96 flex flex-col">
          {/* Header */}
          <div className="px-3 py-2 border-b border-slate-100 bg-slate-50 rounded-t-lg">
            <div className="text-xs font-semibold text-slate-600 mb-1.5">Insert Variable</div>
            <input
              type="text"
              placeholder="Search by name or label..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:border-blue-400 bg-white"
              autoFocus
            />
          </div>

          {/* Variable list + hover preview panel */}
          <div className="flex flex-1 min-h-0">
            {/* List */}
            <div className="overflow-y-auto flex-1">
              {filtered.length === 0 && (
                <div className="p-4 text-sm text-slate-400 text-center">
                  No variables found
                </div>
              )}
              {groups.map((scope) => {
                const items = filtered.filter((v) => v.scope === scope);
                if (items.length === 0) return null;
                return (
                  <div key={scope}>
                    <div className="px-3 py-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider bg-slate-50 border-b border-slate-100 sticky top-0">
                      {SCOPE_LABELS[scope]}
                    </div>
                    {items.map((v) => (
                      <button
                        key={`${scope}-${v.name}`}
                        type="button"
                        className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center gap-2 transition-colors border-b border-slate-50 last:border-0"
                        onClick={() => handleSelect(v)}
                        onMouseEnter={() => setHoveredVar(v)}
                        onMouseLeave={() => setHoveredVar(null)}
                      >
                        <div className="flex flex-col flex-1 min-w-0">
                          <span className="text-sm text-slate-700 font-medium truncate">{v.label}</span>
                          <code className="text-[11px] font-mono text-blue-600 truncate">
                            {`<<${v.name}>>`}
                          </code>
                        </div>
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${SCOPE_COLORS[scope]}`}
                        >
                          {SCOPE_LABELS[scope]}
                        </span>
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>

            {/* Hover detail panel */}
            {hoveredVar && (
              <div className="w-36 shrink-0 border-l border-slate-200 bg-slate-50 p-3 flex flex-col gap-2">
                <div className="text-xs font-semibold text-slate-700">{hoveredVar.label}</div>
                <code className="text-[11px] font-mono text-blue-700 bg-blue-50 px-1.5 py-1 rounded break-all">
                  {`<<${hoveredVar.name}>>`}
                </code>
                {hoveredVar.description && (
                  <div className="text-[11px] text-slate-500 leading-relaxed">{hoveredVar.description}</div>
                )}
                <div className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium self-start ${SCOPE_COLORS[hoveredVar.scope]}`}>
                  {SCOPE_LABELS[hoveredVar.scope]}
                </div>
                <div className="text-[10px] text-slate-500 leading-relaxed">
                  {SCOPE_DESCRIPTIONS[hoveredVar.scope]}
                </div>
                <div className="text-[10px] text-slate-400 mt-auto pt-1 border-t border-slate-200">
                  Click to insert
                </div>
              </div>
            )}
          </div>

          {/* Footer hint */}
          <div className="px-3 py-2 border-t border-slate-100 bg-slate-50 rounded-b-lg">
            <p className="text-[11px] text-slate-400">
              Or type{" "}
              <code className="bg-slate-200 px-1 rounded font-mono text-[10px]">
                {"<<name>>"}
              </code>{" "}
              directly · Use <code className="bg-slate-200 px-1 rounded font-mono text-[10px]">@</code> in value fields
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
